import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocalTime } from '@js-joda/core';
import { formatMessageBundle, formatTimeWithSeconds } from '@unifocus/js-utils';
import { Icons } from '@unifocus/react-icons';
import messages from '../../../../localized-messages';
import { Alert } from '../../../feedback/alert';
import { useForm } from '../Form';
import { FormApi, FormState } from '../FinalFormTypes';
import { Button } from '../../../controls/button';
import { ActivityIndicator } from '../../../feedback/activityindicator';
import { HorizontalSpacer } from '../../../layout/horizontalspacer';

enum AlertMessageType {
   INITIAL="Initial",
   SAVING="Saving",
   SAVED="Saved",
   UNSAVED_CHANGES="Unsaved_Changes",
   COULD_NOT_SAVE_FORM="Could_Not_Save_Form",
   INVALID_FIELD_ERROR="Invalid_Field_Error"
}

const getAllFieldsTouched = (form: FormApi) => {

   const touched = form.getState().touched;
   for (const touchedField in touched) {
      if (touched[touchedField] !== true) {
         return false;
      }
   }
   return true;
};

const getTouchedFieldHasError = (form: FormApi) => {

   const errors = form.getState().errors;
   const touched = form.getState().touched;
   for (const touchedField in touched) {
      if (touched[touchedField] === true && errors && errors[touchedField] !== undefined) {
         return true;
      }
   }
   return false;
};

export const AlertBanner = () => {

   const form = useForm();

   const isAutoSaveForm = form.isAutoSaveForm();

   const [alertMessage, setAlertMessage] = useState<AlertMessageType | undefined>(isAutoSaveForm ? AlertMessageType.INITIAL : undefined);
   const alertMessageRef = useRef<AlertMessageType | undefined>(alertMessage);
   const savedOnTimeRef = useRef<LocalTime | undefined>();

   const submitFn = useCallback(() => (form.submit.bind(form)),
                                // eslint-disable-next-line react-hooks/exhaustive-deps
                                []);

   useEffect(()=>{
      alertMessageRef.current = alertMessage;
   }, [alertMessage]);

   const formSubmittingHandler = useCallback((formState: FormState<any>) => {

      if (formState.submitting) {
         setAlertMessage(AlertMessageType.SAVING);
      }
      else {
         if (alertMessageRef.current === AlertMessageType.SAVING) {
            if (form.getState().submitFailed || form.getState().invalid) {
               if (isAutoSaveForm) {
                  if (getTouchedFieldHasError(form) || form.getState().submitErrors) {
                     setAlertMessage(AlertMessageType.COULD_NOT_SAVE_FORM);
                  }
                  else {
                     savedOnTimeRef.current = LocalTime.now();
                     setAlertMessage(AlertMessageType.SAVED);
                  }
               }
               else {
                  setAlertMessage(AlertMessageType.COULD_NOT_SAVE_FORM);
               }
            }
            else {
               savedOnTimeRef.current = LocalTime.now();
               setAlertMessage(AlertMessageType.SAVED);
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isAutoSaveForm]);

   const formInitialValuesChangedHandler = useCallback(() => {
      if (alertMessageRef.current === AlertMessageType.SAVED || alertMessageRef.current === AlertMessageType.COULD_NOT_SAVE_FORM) {
         if (!isAutoSaveForm) {
            setAlertMessage(undefined);
         }
      }
   }, [isAutoSaveForm]);

   const formValidityChangeHandler = useCallback((formState: FormState<any>) => {

      if (isAutoSaveForm) {
         return;
      }

      if (formState.pristine) {
         if (form.getState().valid) {
            if (alertMessageRef.current === AlertMessageType.INVALID_FIELD_ERROR) {
               setAlertMessage(undefined);
            }
         }
         else {
            if (getAllFieldsTouched(form) && getTouchedFieldHasError(form)) {
               setAlertMessage(AlertMessageType.INVALID_FIELD_ERROR);
            }
         }
      }
      else {
         if (form.getState().valid) {
            if (alertMessageRef.current === AlertMessageType.INVALID_FIELD_ERROR ||
                alertMessageRef.current === AlertMessageType.COULD_NOT_SAVE_FORM) {
               setAlertMessage(undefined);
            }
         }
         else {
            if (alertMessageRef.current !== AlertMessageType.COULD_NOT_SAVE_FORM) {
               if (getAllFieldsTouched(form) && getTouchedFieldHasError(form)) {
                  setAlertMessage(AlertMessageType.INVALID_FIELD_ERROR);
               }
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   },[isAutoSaveForm]);

   useEffect(()=>{

      const formSubmittingHandlerUnsubscribe = form.subscribe(formSubmittingHandler, { submitting: true });
      const formInitialValuesChangedHandlerUnsubscribe = form.subscribe(formInitialValuesChangedHandler, { initialValues: true });
      const formValidityChangeHandlerUnsubscribe = form.subscribe(formValidityChangeHandler, { pristine: true, touched: true, valid: true });

      return () => {
         formSubmittingHandlerUnsubscribe();
         formInitialValuesChangedHandlerUnsubscribe();
         formValidityChangeHandlerUnsubscribe();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   },[isAutoSaveForm, formSubmittingHandler, formInitialValuesChangedHandler, formValidityChangeHandler]);

   const onCloseHandler = useCallback(() => {
      setAlertMessage(undefined);
   }, []);

   if (isAutoSaveForm) {
      return (<AutoSaveBanner alertMessage={alertMessage} savedOnTime={savedOnTimeRef.current} submitFn={submitFn}/>);
   }

   return (<ManualSaveBanner alertMessage={alertMessage} savedOnTime={savedOnTimeRef.current} onCloseHandler={onCloseHandler}/>);
};

interface AutoSaveBannerProps {
   alertMessage?: AlertMessageType;
   savedOnTime?: LocalTime;
   submitFn?: ()=>void;
}

const heightStyle = {
   height: '37px'
};

// const loadingIcon = <LoadingIcon size={'small'} />;

interface SaveNowActionProps {
   onClick?: ()=>void;
   saving?: boolean;
   saved?: boolean;
   disabled?: boolean;
}

const SaveNowAction = (props: SaveNowActionProps) => {

   const isSaving = props.saving;
   const isSaved = props.saved;

   const buttonText = useMemo(()=> (isSaving) ? formatMessageBundle(messages, "res_saving") : formatMessageBundle(messages, "res_saveNow"),[isSaving]);

   const stateIcon = useMemo(() => {
      return (
         <>
            {isSaving ? <ActivityIndicator spinning={true} loadingIndicator={true}/> :
             ((isSaved) ?
                     <Icons.StatusActive iconSet='solid' className={`alert-banner-save-successful-icon`}/> :
                     <HorizontalSpacer/>)}

         </>
      );
   }, [isSaving, isSaved]);

   return (<>
      <Button size={'small'} onClick={props.onClick} disabled={props.disabled || props.saving}>
         {buttonText}
      </Button>
      <HorizontalSpacer/>
      {stateIcon}
   </>);
};

const AutoSaveBanner = (props: AutoSaveBannerProps) => {

   const submitFn = props.submitFn;
   const saveNowOnClickHandler = useCallback(()=>{
      if (submitFn) {
         submitFn();
      }
   }, [submitFn]);

   const saveNowAction = useMemo(()=>(<SaveNowAction onClick={saveNowOnClickHandler}/>),
                                 [saveNowOnClickHandler]);

   let title;
   switch (props.alertMessage) {
      case undefined:
         return (<div style={heightStyle}/>);
      case AlertMessageType.INITIAL:
         title = formatMessageBundle(messages, "res_changesMadeToFormAutomaticallySaved");
         return (<Alert banner={true} title={title} closable={false} type={'info'} showIcon={true} action={saveNowAction}/>);
      case AlertMessageType.UNSAVED_CHANGES:
         title = formatMessageBundle(messages, "res_unsavedChanges");
         return (<Alert banner={true} title={title} closable={false} type={'info'} showIcon={true}/>);
      case AlertMessageType.SAVING:
         title = formatMessageBundle(messages, "res_saving");
         return (<Alert banner={true} title={title} closable={false} type={'info'} showIcon={true} action={saveNowAction}/>);
      case AlertMessageType.SAVED:
         title = formatMessageBundle(messages, "res_saved", [ (props.savedOnTime ? formatTimeWithSeconds(props.savedOnTime) : "") ]);
         return (<Alert banner={true} title={title} closable={false} type={'info'} showIcon={true} action={saveNowAction}/>);
      case AlertMessageType.COULD_NOT_SAVE_FORM:
         title = formatMessageBundle(messages,"res_couldNotSaveFormError");
         return (<Alert banner={true} title={title} closable={false} type={'error'} showIcon={true} />);
      case AlertMessageType.INVALID_FIELD_ERROR:
         title = formatMessageBundle(messages, "res_invalidFieldError");
         return (<Alert banner={true} title={title} closable={false} type={'error'} showIcon={true} />);
   }
};

interface ManualSaveBannerProps extends AutoSaveBannerProps {
   onCloseHandler?: () => void;
}

const ManualSaveBanner = (props: ManualSaveBannerProps) => {

   let title;
   switch (props.alertMessage) {
      case undefined:
         return null;
      case AlertMessageType.INITIAL:
         return null;
      case AlertMessageType.UNSAVED_CHANGES:
         return null;
      case AlertMessageType.SAVING:
         return null;
      case AlertMessageType.SAVED:
         return null;
      case AlertMessageType.COULD_NOT_SAVE_FORM:
         title = formatMessageBundle(messages,"res_couldNotSaveFormError");
         return (<Alert banner={true} title={title} closable={true} type={'error'} showIcon={true} onClose={props.onCloseHandler}/>);
      case AlertMessageType.INVALID_FIELD_ERROR:
         title = formatMessageBundle(messages, "res_invalidFieldError");
         return (<Alert banner={true} title={title} closable={true} type={'error'} showIcon={true} onClose={props.onCloseHandler}/>);
   }
};
